/**
 * FB Monitor API Module
 * Handles all Facebook Content Monitor API calls
 */

import { fetchWithAuth } from './client';

// FB Monitor API endpoint
const FB_MONITOR_API = process.env.REACT_APP_FB_MONITOR_API || 'https://api.cloudmortgage.io';

/**
 * Parse API error response and extract user-friendly message
 * Handles the subscription_required error format from the backend
 * @param {Response} response - Fetch response object
 * @param {string} fallbackMessage - Default error message
 * @returns {Promise<Error>}
 */
const parseApiError = async (response, fallbackMessage) => {
    try {
        const error = await response.json();

        // Handle subscription_required error (403)
        if (response.status === 403 && error.detail?.error === 'subscription_required') {
            return new Error(error.detail.message || 'This feature requires an Ultra or Enterprise subscription');
        }

        // Handle standard error formats
        if (typeof error.detail === 'string') {
            return new Error(error.detail);
        }
        if (error.detail?.message) {
            return new Error(error.detail.message);
        }
        if (error.message) {
            return new Error(error.message);
        }

        return new Error(fallbackMessage);
    } catch {
        return new Error(fallbackMessage);
    }
};

// ============================================================================
// Session Management
// ============================================================================

/**
 * Get current session status
 * @returns {Promise<{ status: string, expires_at: string|null, days_remaining: number|null }>}
 */
export const getSessionStatus = async () => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/session/status`);
    if (!response.ok) {
        if (response.status === 404) {
            return { status: 'no_session', expires_at: null, days_remaining: null };
        }
        throw await parseApiError(response, 'Failed to fetch session status');
    }
    return response.json();
};

/**
 * Create a new Facebook session
 * @param {Object} data - Session data
 * @param {string} data.cookies - Facebook session cookies (JSON string)
 * @returns {Promise<{ status: string, expires_at: string }>}
 */
export const createSession = async (data) => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to create session');
    }
    return response.json();
};

/**
 * Delete/disconnect the current session
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteSession = async () => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/session`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to delete session');
    }
    return response.json();
};

// ============================================================================
// Configuration
// ============================================================================

/**
 * Get monitor configuration
 * @returns {Promise<{ delivery_email: string, delivery_time: string, timezone: string, is_active: boolean }>}
 */
export const getConfig = async () => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/config`);
    if (!response.ok) {
        if (response.status === 404) {
            return null;
        }
        throw await parseApiError(response, 'Failed to fetch config');
    }
    return response.json();
};

/**
 * Update monitor configuration
 * @param {Object} data - Config data
 * @param {string} [data.delivery_email] - Email for report delivery
 * @param {string} [data.delivery_time] - Time for report delivery (HH:MM)
 * @param {string} [data.timezone] - Timezone for delivery
 * @param {boolean} [data.is_active] - Whether monitoring is active
 * @returns {Promise<Object>}
 */
export const updateConfig = async (data) => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to update config');
    }
    return response.json();
};

// ============================================================================
// Sources Management
// ============================================================================

/**
 * Get list of monitored sources
 * @returns {Promise<{ sources: Array, total: number, limit: number }>}
 */
export const getSources = async () => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/sources`);
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to fetch sources');
    }
    return response.json();
};

/**
 * Add a new source to monitor
 * @param {Object} data - Source data
 * @param {string} data.url - Facebook group/page URL
 * @param {string} [data.name] - Display name for the source
 * @param {string} data.type - Source type ('group' or 'page')
 * @returns {Promise<Object>}
 */
export const addSource = async (data) => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/sources`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to add source');
    }
    return response.json();
};

/**
 * Update an existing source
 * @param {string} id - Source ID
 * @param {Object} data - Updated source data
 * @param {string} [data.name] - Display name
 * @param {boolean} [data.is_active] - Whether source is active
 * @returns {Promise<Object>}
 */
export const updateSource = async (id, data) => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/sources/${id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
    });
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to update source');
    }
    return response.json();
};

/**
 * Delete a source
 * @param {string} id - Source ID
 * @returns {Promise<{ success: boolean }>}
 */
export const deleteSource = async (id) => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/sources/${id}`, {
        method: 'DELETE',
    });
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to delete source');
    }
    return response.json();
};

// ============================================================================
// Reports
// ============================================================================

/**
 * Get reports with optional filters
 * @param {Object} [filters] - Optional filters
 * @param {number} [filters.limit] - Number of reports to return
 * @param {number} [filters.offset] - Offset for pagination
 * @returns {Promise<{ reports: Array, total: number, has_more: boolean }>}
 */
export const getReports = async (filters = {}) => {
    const params = new URLSearchParams();
    if (filters.limit) params.append('limit', filters.limit.toString());
    if (filters.offset) params.append('offset', filters.offset.toString());

    const queryString = params.toString();
    const url = `${FB_MONITOR_API}/api/fb-monitor/reports${queryString ? `?${queryString}` : ''}`;

    const response = await fetchWithAuth(url);
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to fetch reports');
    }
    return response.json();
};

/**
 * Get the latest report
 * @returns {Promise<Object|null>}
 */
export const getLatestReport = async () => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/reports/latest`);
    if (!response.ok) {
        if (response.status === 404) {
            return null;
        }
        throw await parseApiError(response, 'Failed to fetch latest report');
    }
    return response.json();
};

/**
 * Get a specific report by date
 * @param {string} date - Report date (YYYY-MM-DD)
 * @returns {Promise<Object>}
 */
export const getReportByDate = async (date) => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/reports/${date}`);
    if (!response.ok) {
        if (response.status === 404) {
            return null;
        }
        throw await parseApiError(response, 'Failed to fetch report');
    }
    return response.json();
};

// ============================================================================
// Statistics
// ============================================================================

/**
 * Get monitor statistics
 * @returns {Promise<{
 *   total_reports: number,
 *   total_posts_analyzed: number,
 *   total_credits_used: number,
 *   last_report_date: string|null,
 *   active_sources: number
 * }>}
 */
export const getStats = async () => {
    const response = await fetchWithAuth(`${FB_MONITOR_API}/api/fb-monitor/stats`);
    if (!response.ok) {
        throw await parseApiError(response, 'Failed to fetch stats');
    }
    return response.json();
};
